import React, { useCallback, useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtDate } from "@/lib/dt";
import DateField from "@/components/DateField";
import Ref from "@/components/Ref";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, X, Check, ExternalLink } from "lucide-react";
import { RestatementList } from "@/components/restatement/RestatementViews";   // Prior-Period Restatement · Phase 3b

/**
 * N2 — 311(a) section 3 renderer. Single source of truth is the
 * `tcwg_311a_log` collection; add + edit only (no delete — R7).
 *
 * Extended columns per N1: category, linked_ref, response, follow-up.
 *
 * C3b · P2..P5 fixes (Sep 29 2026):
 *   * P2 — Native `<Input type="date">` swapped for the shared
 *          `DateField` (DD-MMM-YYYY picker, LTR-isolated for AR).
 *   * P3 — `isReview` prop swaps `Auditor's responsibilities` category
 *          label → `Practitioner's responsibilities` on Review files.
 *   * P4 — Table wraps at the container width (no more sideways scroll
 *          hiding "الرد" / "متابعة" / ✓ ✗). Uses a two-column CSS grid
 *          per row on narrow screens, expands to a table on wide ones.
 *   * P5 — Section title comes from i18n; save errors use `toErrMsg`
 *          so Arabic screens see Arabic copy (no raw HTTP status).
 */
const CATEGORIES = ["auditor_responsibilities", "planned_scope_and_timing",
                    "significant_findings", "independence_listed", "other"];
const MODES = ["oral", "written"];

export default function Tcwg311aLog({ engagementId, preset = null, isReview = false, showSectionTitle = false }) {
  const { currentEngId } = useEngagement();
  const engId = engagementId || currentEngId;
  const { t, lang } = useI18n();
  const [rows, setRows] = useState([]);
  const [restatements, setRestatements] = useState([]);   // Restatement Phase 3b
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(newDraft(preset));

  const load = useCallback(async () => {
    if (!engId) return;
    setLoading(true);
    try {
      const r = await api.get(`/engagements/${engId}/311a/log`, { _silent: true });
      setRows(r.data?.rows || []);
      setRestatements(r.data?.restatements || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [engId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Consume any preset supplied when the component is opened from
    // W9's "Add to 311(a) log" button (pre-fills category + linked_ref).
    if (preset) setDraft(() => ({ ...newDraft(preset) }));
    // eslint-disable-next-line
  }, [preset && preset.linked_ref, preset && preset.category]);

  const startNew = () => { setEditingId("__new__"); setDraft(newDraft(preset)); };
  const startEdit = (row) => { setEditingId(row.id); setDraft({ ...row }); };
  const cancel = () => { setEditingId(null); setDraft(newDraft(preset)); };

  const save = async () => {
    // Client-side validation surfaces as translated toasts, not a 400
    // round-trip. Matches EN + AR strings under `tcwg.errXxx`.
    if (!draft.category) { toast.error(t("tcwg.errCategoryRequired")); return; }
    if (!(draft.matter || "").trim()) { toast.error(t("tcwg.errMatterRequired")); return; }
    try {
      if (editingId === "__new__") {
        await api.post(`/engagements/${engId}/311a/log`, draft, { _silent: true });
      } else {
        await api.patch(`/engagements/${engId}/311a/log/${editingId}`, draft, { _silent: true });
      }
      toast.success(t("tcwg.savedOk"));
      cancel();
      await load();
    } catch (e) {
      // P5 — no raw "Request failed with status code 500". `toErrMsg`
      // routes through the i18n `errors.<CODE>` map when the backend
      // sent one, and falls back to the translated generic on the AR
      // screen.
      toast.error(toErrMsg(e, t("tcwg.saveFailed")));
    }
  };

  // P3 — "Auditor's responsibilities" ↔ "Practitioner's responsibilities"
  // on Review engagements. We keep the same category key on the wire so
  // audit trails and stored rows stay stable across engagement types —
  // only the visible label swaps.
  const catLabel = (c) => {
    if (isReview && c === "auditor_responsibilities") {
      return t("tcwg.catReview.auditor_responsibilities") || t(`tcwg.cat.${c}`) || c;
    }
    return t(`tcwg.cat.${c}`) || c;
  };
  const modeLabel = (m) => t(`tcwg.mode.${m}`) || m;

  return (
    <div className="space-y-2" data-testid="tcwg-311a-log">
      <RestatementList items={restatements} testid="tcwg-restatements" />
      {showSectionTitle && (
        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100"
             data-testid="tcwg-311a-section-title">
          {t("tcwg.sectionTitle")}
        </div>
      )}
      {isReview && (
        <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200"
             data-testid="tcwg-311a-review-note">
          {t("tcwg.reviewNote")}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-muted-foreground">
          {t("tcwg.tableHint")}
        </div>
        <Button size="sm" variant="outline" onClick={startNew}
                disabled={editingId !== null}
                data-testid="tcwg-311a-log-add">
          <Plus className="h-3 w-3 me-1" /> {t("tcwg.addBtn")}
        </Button>
      </div>

      {/* P4 — Card-per-row layout. No horizontal scroll; every field
             (including الرد / متابعة / ✓ ✗) always visible inside the
             container width. Two-column grid on ≥ md, single column on
             narrow. */}
      <div className="space-y-2" data-testid="tcwg-311a-log-list">
        {editingId === "__new__" && (
          <EditCard draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel}
                    t={t} lang={lang} catLabel={catLabel} modeLabel={modeLabel} />
        )}
        {rows.length === 0 && editingId !== "__new__" && !loading && (
          <div className="rounded border border-dashed p-4 text-center text-[12px] text-muted-foreground"
               data-testid="tcwg-311a-log-empty">
            {t("tcwg.empty")}
          </div>
        )}
        {rows.map((row) => (editingId === row.id ? (
          <EditCard key={row.id} draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel}
                    t={t} lang={lang} catLabel={catLabel} modeLabel={modeLabel} />
        ) : (
          <ReadCard key={row.id} row={row} t={t}
                    catLabel={catLabel} modeLabel={modeLabel}
                    onEdit={() => startEdit(row)}
                    editingBlocked={editingId !== null} />
        )))}
      </div>
    </div>
  );
}

function ReadCard({ row, t, catLabel, modeLabel, onEdit, editingBlocked }) {
  return (
    <div className="rounded border bg-white p-2 dark:bg-slate-900/40"
         data-testid={`tcwg-311a-log-row-${row.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="text-[10px]">{catLabel(row.category)}</Badge>
            <span className="font-mono text-[10px] text-muted-foreground">
              {fmtDate(row.date_communicated) || "—"}
            </span>
            <span className="text-[10px] text-muted-foreground">· {modeLabel(row.mode)}</span>
            {row.linked_ref && (() => {
              /* Q6 — Show a readable label for known linked-refs
                 (e.g. "W9-tcwg-communicated" → "W9 — أُبلغ المكلفون
                 بالحوكمة") instead of the raw storage key. Falls back
                 to the raw ref for unknown values so future links
                 don't disappear. */
              const meta = t(`tcwg.linkedRefs.${row.linked_ref}`);
              const label = (meta && typeof meta === "object" && meta.label)
                             || t(`tcwg.linkedRefs.${row.linked_ref}.label`);
              const href = (meta && typeof meta === "object" && meta.href)
                             || t(`tcwg.linkedRefs.${row.linked_ref}.href`);
              if (href && label && !label.startsWith("tcwg.linkedRefs.")) {
                return (
                  <RouterLink to={href}
                              className="inline-flex items-center gap-1 rounded bg-sky-50 px-1.5 py-0.5 text-[10px] text-sky-800 hover:bg-sky-100 dark:bg-sky-950/40 dark:text-sky-200"
                              data-testid={`tcwg-311a-log-linked-${row.id}`}>
                    {label} <ExternalLink className="h-3 w-3" />
                  </RouterLink>
                );
              }
              return <span className="font-mono text-[10px]"><Ref>{row.linked_ref}</Ref></span>;
            })()}
          </div>
          <div className="mt-1 text-[12px] text-slate-800 dark:text-slate-100 break-words">
            {row.matter}
          </div>
          <div className="mt-1 grid grid-cols-1 gap-x-4 gap-y-1 text-[11px] md:grid-cols-2">
            <div>
              <span className="text-muted-foreground">{t("tcwg.col.to")}: </span>
              <span>{row.communicated_to_name || "—"}
                {row.communicated_to_role ? ` (${row.communicated_to_role})` : ""}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t("tcwg.col.followup")}: </span>
              <span>{row.followup ? t("common.yes") : t("common.no")}</span>
            </div>
            {row.response && (
              <div className="md:col-span-2">
                <span className="text-muted-foreground">{t("tcwg.col.response")}: </span>
                <span className="break-words">{row.response}</span>
              </div>
            )}
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={onEdit}
                disabled={editingBlocked}
                data-testid={`tcwg-311a-log-edit-${row.id}`}
                aria-label={t("common.edit") || "Edit"}>
          <Pencil className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function EditCard({ draft, setDraft, onSave, onCancel, t, lang, catLabel, modeLabel }) {
  return (
    <div className="rounded border-2 border-amber-300 bg-amber-50/60 p-3 dark:bg-amber-950/10"
         data-testid="tcwg-311a-log-edit-card">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <div>
          <div className="mb-1 text-[10px] uppercase text-muted-foreground">{t("tcwg.col.category")}</div>
          <Select value={draft.category || ""} onValueChange={(v) => setDraft({ ...draft, category: v })}>
            <SelectTrigger className="h-8 text-[12px]" data-testid="tcwg-311a-log-cat">
              <SelectValue placeholder={t("tcwg.col.category")} />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="mb-1 text-[10px] uppercase text-muted-foreground">{t("tcwg.col.date")}</div>
          <DateField value={draft.date_communicated || ""}
                     onCommit={(v) => setDraft({ ...draft, date_communicated: v })}
                     placeholder="DD-MMM-YYYY"
                     testid="tcwg-311a-log-date" />
        </div>
        <div className="md:col-span-2">
          <div className="mb-1 text-[10px] uppercase text-muted-foreground">{t("tcwg.col.matter")}</div>
          <Textarea value={draft.matter || ""} onChange={(e) => setDraft({ ...draft, matter: e.target.value })}
                    className="min-h-[64px] text-[12px]" data-testid="tcwg-311a-log-matter" />
        </div>
        <div>
          <div className="mb-1 text-[10px] uppercase text-muted-foreground">{t("tcwg.col.linkedRef")}</div>
          <Input value={draft.linked_ref || ""} onChange={(e) => setDraft({ ...draft, linked_ref: e.target.value })}
                 className="h-8 font-mono text-[11px]" data-testid="tcwg-311a-log-linkedref" />
        </div>
        <div>
          <div className="mb-1 text-[10px] uppercase text-muted-foreground">{t("tcwg.col.mode")}</div>
          <Select value={draft.mode || "oral"} onValueChange={(v) => setDraft({ ...draft, mode: v })}>
            <SelectTrigger className="h-8 text-[12px]" data-testid="tcwg-311a-log-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODES.map((m) => (
                <SelectItem key={m} value={m}>{modeLabel(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="mb-1 text-[10px] uppercase text-muted-foreground">{t("tcwg.col.to")}</div>
          <Input value={draft.communicated_to_name || ""}
                 onChange={(e) => setDraft({ ...draft, communicated_to_name: e.target.value })}
                 placeholder={t("tcwg.col.to")} className="h-8 text-[12px]"
                 data-testid="tcwg-311a-log-toname" />
        </div>
        <div>
          <div className="mb-1 text-[10px] uppercase text-muted-foreground">{t("tcwg.col.toRole")}</div>
          <Input value={draft.communicated_to_role || ""}
                 onChange={(e) => setDraft({ ...draft, communicated_to_role: e.target.value })}
                 placeholder={t("tcwg.col.toRole")} className="h-8 text-[12px]"
                 data-testid="tcwg-311a-log-torole" />
        </div>
        <div className="md:col-span-2">
          <div className="mb-1 text-[10px] uppercase text-muted-foreground">{t("tcwg.col.response")}</div>
          <Textarea value={draft.response || ""} onChange={(e) => setDraft({ ...draft, response: e.target.value })}
                    className="min-h-[56px] text-[12px]" data-testid="tcwg-311a-log-response" />
        </div>
        <div className="md:col-span-2 flex items-center justify-between">
          <label className="inline-flex items-center gap-1 text-[12px]"
                 data-testid="tcwg-311a-log-followup-wrap">
            <input type="checkbox" checked={!!draft.followup}
                   onChange={(e) => setDraft({ ...draft, followup: e.target.checked })}
                   data-testid="tcwg-311a-log-followup" />
            <span>{t("tcwg.col.followup")}</span>
          </label>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={onCancel} data-testid="tcwg-311a-log-cancel"
                    aria-label={t("common.cancel") || "Cancel"}>
              <X className="h-3 w-3" />
            </Button>
            <Button size="sm" onClick={onSave} data-testid="tcwg-311a-log-save"
                    aria-label={t("common.save") || "Save"}>
              <Check className="h-3 w-3 me-1" />
              {t("common.save") || "Save"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function newDraft(preset) {
  return {
    category: (preset && preset.category) || "",
    matter: "",
    linked_ref: (preset && preset.linked_ref) || "",
    date_communicated: "",
    mode: "oral",
    communicated_to_name: "",
    communicated_to_role: "",
    response: "",
    followup: false,
  };
}
